// JWT authentication utilities

// State variables for token refresh mechanism
let isRefreshing = false;
let refreshPromise = null;

// Parse tokens from URL parameter
export const parseAuthFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const authStatus = urlParams.get('auth_status');
  
  if (authStatus) {
    try {
      // Remove auth_status from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Decode and parse the auth_status
      const decodedData = atob(authStatus);
      const { access_token, refresh_token } = JSON.parse(decodedData);
      
      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      return true;
    } catch (error) {
      console.error('Error parsing auth status:', error);
      return false;
    }
  }
  return false;
};

// Get stored access token
export const getAccessToken = () => {
  return localStorage.getItem('access_token');
};

// Get stored refresh token
export const getRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

// Check if token needs refresh by decoding it and checking expiration
export const isTokenExpired = () => {
  const token = getAccessToken();
  if (!token) return true;
  
  try {
    // Get the expiration from the token (JWT format)
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check if the token is expired or about to expire (within 60 seconds)
    const now = Math.floor(Date.now() / 1000);
    return payload.exp <= (now + 60);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true; // If we can't check, assume it needs refresh
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getAccessToken();
};

// Clear authentication tokens
export const clearAuth = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// Authenticated fetch function
export const authFetch = async (url, options = {}) => {
  const token = getAccessToken();
  
  // Set up headers with authorization
  const headers = {
    ...(options.headers || {}),
    'Authorization': `Bearer ${token}`
  };
  
  // Set credentials to "omit" to prevent sending cookies
  const fetchOptions = {
    ...options,
    headers,
    credentials: 'omit'
  };
  
  // Attempt the fetch
  let response = await fetch(url, fetchOptions);
  
  // If unauthorized, try to refresh the token
  if (response.status === 401) {
    // Use the centralized refresh mechanism to avoid multiple concurrent refreshes
    let refreshed;
    
    if (!isRefreshing) {
      // Start a new refresh process
      isRefreshing = true;
      refreshPromise = doRefreshToken();
    }
    
    try {
      // Wait for the current refresh process to complete
      refreshed = await refreshPromise;
    } finally {
      // Reset refresh state if this was the initiating request
      if (refreshPromise === refreshPromise) {
        isRefreshing = false;
        refreshPromise = null;
      }
    }
    
    if (refreshed) {
      // Retry with new token
      fetchOptions.headers.Authorization = `Bearer ${getAccessToken()}`;
      response = await fetch(url, fetchOptions);
    } else {
      // Redirect to login if refresh failed
      redirectToLogin();
    }
  }
  
  return response;
};

// The actual token refresh implementation
const doRefreshToken = async () => {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) return false;
  
  try {
    const response = await fetch('/api/refresh', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${refreshTokenValue}`
      },
      credentials: 'omit' // Also prevent cookies in refresh requests
    });
    
    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      // Store the rotated refresh token from the backend
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      return true;
    } else {
      clearAuth();
      return false;
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    clearAuth();
    return false;
  }
};

// Public refresh token function that uses the centralized mechanism
export const refreshToken = async () => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = doRefreshToken();
  }
  
  try {
    return await refreshPromise;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
};

// Delete all non-HttpOnly cookies visible to JS except those in cookiesToKeep.
export const clearVisibleCookies = (cookiesToKeep = []) => {
  try {
    document.cookie.split(';').forEach(raw => {
      const [name] = raw.split('=');
      const trimmed = name && name.trim();
      if (!trimmed) return;
      if (cookiesToKeep.includes(trimmed)) return;

      // Delete for current host
      document.cookie = `${trimmed}=; Max-Age=0; path=/`;
      // Delete for parent domain
      const hostParts = window.location.hostname.split('.');
      if (hostParts.length > 2) {
        const parentDomain = hostParts.slice(-2).join('.');
        document.cookie = `${trimmed}=; Max-Age=0; path=/; domain=.${parentDomain}`;
      }
    });
  } catch (err) {
    console.warn('Failed to clear cookies:', err);
  }
};

// Helper to clear cookies then redirect to login
export const redirectToLogin = (cookiesToKeep = []) => {
  clearVisibleCookies(cookiesToKeep);
  window.location.href = '/api/login';
}; 
